import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDashboardContext } from '../../../contexts/DashboardContext';
import { useManagerContext } from '../../../contexts/ManagerContext';
import { useDocumentContext } from '../../../contexts/DocumentContext';
import gridStyles from '../../../styles/DashboardV2.module.css';

// Manager widgets
import AgencyComplianceWidget      from './manager/AgencyComplianceWidget';
import AtRiskAgentsWidget          from './manager/AtRiskAgentsWidget';
import TeamComplianceChartWidget   from './manager/TeamComplianceChartWidget';
import ComplianceRiskForecastWidget from './manager/ComplianceRiskForecastWidget';
import RecentTeamActivityWidget    from './manager/RecentTeamActivityWidget';

interface AgencyDashboardProps {
    userDetails: any;
}

export const AgencyDashboard: React.FC<AgencyDashboardProps> = ({ userDetails }) => {
    const navigate = useNavigate();
    const { agencyStats, companyStats, riskAgents, agencyChartData } = useDashboardContext();
    const { agencyUsers, companyOverallStats } = useManagerContext();
    const { documentTypes } = useDocumentContext();

    let overallAgentStats = agencyUsers.find(agent => agent.user.id == userDetails.id)?.stats.overall;
    
    React.useEffect(() => {
        if (userDetails?.role !== 'Manager' && userDetails?.role !== 'Admin') {
            navigate('/Account');
        }
    }, [userDetails, navigate]);

    const companyRate = companyStats?.completionRate ?? 0;

    // Build recent team activity from uploaded documents across all agents
    const teamActivities = useMemo(() => {
        const activities: Array<{ id: string; agentName: string; action: string; createdAt: string }> = [];
        (agencyUsers ?? []).forEach(userData => {
            const name = userData.user?.fullName ?? userData.user?.displayName ?? userData.user?.email ?? 'Unknown';
            (userData.documents ?? []).forEach((doc: any) => {
                activities.push({
                    id:        doc.id ?? doc._id ?? String(Math.random()),
                    agentName: name,
                    action:    `uploaded ${doc.title ?? doc.fileName ?? 'a document'}`,
                    createdAt: doc.createdAt ?? doc.updatedAt ?? new Date().toISOString(),
                });
            });
        });
        return activities
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
            .slice(0, 15);
    }, [agencyUsers]);

    return (
        <div className={gridStyles.dashboardPage}>
            <div className={gridStyles.dashboardHeader}>
                <h1 className={gridStyles.dashboardTitle}>Agency Dashboard</h1>
                <p className={gridStyles.dashboardSubtitle}>
                    Team compliance, risk management, and activity overview
                </p>
            </div>

            <div className={gridStyles.dashboardGrid}>

                {/* ── Row 1 ── */}
                <div className={gridStyles.col3}>
                    <AgencyComplianceWidget
                        label="Agents Compliance"
                        completionRate={agencyStats?.completionRate}
                        completedItems={agencyStats?.completedItems ?? 0}
                        totalItems={agencyStats?.totalItems ?? 0}
                    />
                </div>

                <div className={gridStyles.col3}>
                    <AgencyComplianceWidget
                        label="Company Compliance"
                        completionRate={companyRate}
                        completedItems={companyStats?.completedItems ?? 0}
                        totalItems={companyStats?.totalItems ?? 0}
                        iconVariant="purple"
                    />
                </div>

                <div className={gridStyles.col6}>
                    <AtRiskAgentsWidget
                        agents={riskAgents}
                    />
                </div>

                {/* ── Row 2 ── */}
                <div className={gridStyles.col8}>
                    <TeamComplianceChartWidget agents={agencyChartData} />
                </div>

                <div className={gridStyles.col4}>
                    <ComplianceRiskForecastWidget agencyUsers={agencyUsers ?? []} documentTypes={documentTypes} />
                </div>

                {/* ── Row 3 ── */}
                <div className={gridStyles.col12}>
                    <RecentTeamActivityWidget activities={teamActivities} />
                </div>

            </div>
        </div>
    );
};

export default AgencyDashboard;
